import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { isLogged } from '../../services/helpers'
import { listOffers } from '../../services/offers'
import OfferCard from '../../components/OfferCard'
import Logo from '../../asset/images/logo.svg'
import {
  TicketIcon,
  ChartBarIcon,
  CreditCardIcon,
  UserGroupIcon,
  CheckCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  BoltIcon,
  ClockIcon,
  GlobeAltIcon,
  StarIcon,
  TagIcon,
  HeartIcon,
  FireIcon,
  TrophyIcon,
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'

const LandingPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [offers, setOffers] = useState([])
  const [loadingOffers, setLoadingOffers] = useState(true)
  const [offersSearchQuery, setOffersSearchQuery] = useState('')

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Handle scroll to offers section when navigating from offer details
  useEffect(() => {
    if (location.state?.scrollToOffers) {
      setTimeout(() => {
        const offersSection = document.getElementById('offers')
        if (offersSection) {
          offersSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
          // Clear the state to prevent scrolling on subsequent renders
          navigate(location.pathname, { replace: true, state: {} })
        }
      }, 300)
    }
  }, [location.state, navigate, location.pathname])

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await listOffers({ limit: 6 })
        if (res.status === 200 && Array.isArray(res.data)) {
          // Show only first 6 offers
          setOffers(res.data.slice(0, 6))
        }
      } catch (e) {
        console.error("Failed to fetch offers", e)
      } finally {
        setLoadingOffers(false)
      }
    }
    fetchOffers()
  }, [])

  const handleGetStarted = () => {
    navigate('/center/signup')
  }

  const handleOffersSearch = (e) => {
    e.preventDefault()
    navigate(`/offers?search=${offersSearchQuery}`)
  }

  const navLinks = [
    { name: 'Offers', href: '/offers', internal: true },
    { name: 'Use Cases', href: '#dashboards', internal: false },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/40 font-sans text-slate-900 selection:bg-emerald-100 selection:text-emerald-900">

      {/* Navigation */}
      <nav
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'bg-white/90 backdrop-blur-xl shadow-lg' 
            : 'bg-white/70 backdrop-blur-md'
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-1.5 group">
              <img
                src={Logo}
                alt="Obule"
                className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
              />
              <span className="text-3xl font-semibold tracking-wide text-slate-900">
                bule
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex md:gap-x-2">
              {navLinks.map((link) => (
                link.internal ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="px-5 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all duration-300"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="px-5 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all duration-300"
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/login"
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-slate-700 hover:text-emerald-700 hover:bg-stone-50 transition-all duration-300"
              >
                Sign in
              </Link>
              <button
                onClick={handleGetStarted}
                className="relative group px-7 py-3 rounded-2xl text-sm font-semibold text-white shadow-xl transition-all duration-500 hover:scale-105 active:scale-95 overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                }}
              >
                <span className="relative z-10">Create Event</span>
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: 'linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)'
                  }}
                ></div>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden">
              <button
                type="button"
                className="-m-2.5 inline-flex items-center justify-center rounded-lg p-2.5 text-slate-700 hover:bg-slate-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200/50 bg-white/95 backdrop-blur-xl shadow-xl">
            <div className="space-y-2 px-4 pb-6 pt-4">
              {navLinks.map((link) => (
                link.internal ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="block px-5 py-3.5 rounded-xl text-base font-medium text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all duration-300"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="block px-5 py-3.5 rounded-xl text-base font-medium text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all duration-300"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                )
              ))}
              <div className="mt-6 border-t border-slate-200/50 pt-6 flex flex-col gap-4">
                <Link
                  to="/login"
                  className="block w-full text-center rounded-xl px-5 py-3.5 text-base font-medium text-slate-700 hover:text-emerald-700 hover:bg-stone-50 transition-all duration-300"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign in
                </Link>
                <button
                  onClick={() => {
                    handleGetStarted()
                    setMobileMenuOpen(false)
                  }}
                  className="block w-full rounded-2xl px-6 py-4 text-center text-base font-semibold text-white shadow-xl transition-all duration-500"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                  }}
                >
                  Create Event
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section - Organic/Fluid */}
        <div className="relative isolate pt-32 pb-20 overflow-hidden" style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 20%, #fef3c7 40%, #d1fae5 60%, #a7f3d0 80%, #ecfdf5 100%)'
        }}>
          {/* Organic blob background decorations */}
          <div 
            className="absolute top-0 right-0 w-[500px] h-[500px] opacity-30 blur-3xl animate-pulse"
            style={{
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
              transform: 'rotate(45deg) translate(20%, -20%)',
              animation: 'float 8s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute bottom-0 left-0 w-[600px] h-[600px] opacity-25 blur-3xl"
            style={{
              clipPath: 'polygon(20% 0%, 80% 0%, 100% 20%, 100% 80%, 80% 100%, 20% 100%, 0% 80%, 0% 20%)',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
              transform: 'rotate(-30deg) translate(-15%, 15%)',
              animation: 'float 10s ease-in-out infinite',
              animationDelay: '2s'
            }}
          ></div>
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] opacity-20 blur-3xl"
            style={{
              clipPath: 'polygon(40% 0%, 60% 0%, 100% 40%, 100% 60%, 60% 100%, 40% 100%, 0% 60%, 0% 40%)',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
              transform: 'rotate(15deg)',
              animation: 'float 12s ease-in-out infinite',
              animationDelay: '4s'
            }}
          ></div>
          
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="mx-auto max-w-5xl">
              <div className="text-center">
                {/* Flash Sale Banner */}
                <div 
                  className="inline-flex items-center gap-2 text-white px-6 py-2.5 mb-6 rounded-full shadow-xl relative group"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)'
                  }}
                >
                  <BoltIcon className="h-4 w-4" />
                  <span className="text-xs font-semibold tracking-wide">FLASH SALE - Limited Time Only!</span>
                  <div 
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)'
                    }}
                  ></div>
                </div>
                
                <h1 className="text-5xl sm:text-6xl md:text-7xl font-light tracking-wide text-slate-900 mb-6 leading-[1.1]" style={{
                  letterSpacing: '0.02em',
                  fontWeight: 300
                }}>
                  <span style={{
                    background: 'linear-gradient(135deg, #059669 0%, #047857 50%, #065f46 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'inline-block'
                  }}>Exclusive Deals</span>
                  <br />
                  <span className="text-slate-800">Up to 70% Off</span>
                </h1>
                
                <p className="text-lg sm:text-xl text-slate-700 max-w-3xl mx-auto font-light mb-10 leading-relaxed" style={{
                  letterSpacing: '0.01em',
                  lineHeight: '1.8'
                }}>
                  Discover amazing discounted tickets to the best events. Limited time offers you won't find anywhere else!
                </p>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={() => navigate('/offers')}
                    className="group relative px-8 py-4 rounded-2xl text-base font-medium text-white shadow-2xl transition-all duration-500 hover:scale-105 active:scale-95 overflow-hidden"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                    }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      Shop Deals Now
                      <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-2 transition-transform duration-500" />
                    </span>
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: 'linear-gradient(135deg, #34d399 0%, #10b981 50%, #059669 100%)'
                      }}
                    ></div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Offers Section - Organic */}
        <div id="offers" className="relative py-24 overflow-hidden" style={{
          background: 'linear-gradient(180deg, #fef3c7 0%, #fde68a 15%, #ffffff 30%, #ecfdf5 70%, #d1fae5 100%)'
        }}>
          {/* Organic background blobs */}
          <div 
            className="absolute top-0 right-0 w-[400px] h-[400px] opacity-20 blur-3xl"
            style={{
              clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              transform: 'rotate(25deg) translate(10%, -10%)',
              animation: 'float 15s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute bottom-0 left-0 w-[500px] h-[500px] opacity-15 blur-3xl"
            style={{
              clipPath: 'polygon(35% 0%, 65% 0%, 100% 35%, 100% 65%, 65% 100%, 35% 100%, 0% 65%, 0% 35%)',
              background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
              transform: 'rotate(-20deg) translate(-10%, 10%)',
              animation: 'float 18s ease-in-out infinite',
              animationDelay: '3s'
            }}
          ></div>
          
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex items-center justify-end mb-10">
              <Link 
                to="/offers" 
                className="group flex items-center gap-2 text-base font-medium text-emerald-700 hover:text-emerald-800 transition-all duration-300"
              >
                <span>View all deals</span>
                <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-2 transition-transform duration-500" />
              </Link>
            </div>

            {/* Search Bar */}
            <div className="mb-16 max-w-3xl mx-auto">
              <form onSubmit={handleOffersSearch} className="relative flex items-center group">
                <div 
                  className="absolute inset-0 rounded-2xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  }}
                ></div>
                <div 
                  className="relative w-full flex items-center bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl overflow-hidden"
                >
                  <MagnifyingGlassIcon className="absolute left-8 h-6 w-6 text-slate-400 group-focus-within:text-emerald-600 transition-colors duration-300" />
                  <input
                    type="text"
                    value={offersSearchQuery}
                    onChange={(e) => setOffersSearchQuery(e.target.value)}
                    placeholder="Search for offers, deals, or discounts..."
                    className="block w-full border-0 py-6 pl-20 pr-40 text-slate-900 placeholder:text-slate-400 focus:ring-0 text-base font-light"
                    style={{ letterSpacing: '0.01em' }}
                  />
                  <button
                    type="submit"
                    className="absolute right-3 top-3 bottom-3 rounded-xl px-10 text-sm font-medium text-white transition-all duration-500 hover:scale-105 active:scale-95 shadow-lg"
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)'
                    }}
                  >
                    Search
                  </button>
                </div>
              </form>
            </div>

            {loadingOffers ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 animate-pulse">
                {[1, 2, 3].map(i => (
                  <div 
                    key={i} 
                    className="h-[420px] bg-slate-200/50"
                    style={{
                      clipPath: 'polygon(10% 0%, 90% 0%, 100% 20%, 100% 80%, 90% 100%, 10% 100%, 0% 80%, 0% 20%)'
                    }}
                  ></div>
                ))}
              </div>
            ) : offers.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                {offers.map((offer, index) => (
                  <div key={offer._id || offer.id} className="relative">
                    {/* Featured Badge for first offer - Small tag on left top edge */}
                    {index === 0 && (
                      <div 
                        className="absolute top-2 left-2 z-20 rounded-lg text-white px-2 py-1 text-xs font-semibold shadow-lg whitespace-nowrap"
                        style={{
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)'
                        }}
                      >
                        DEAL OF THE DAY
                      </div>
                    )}
                    <div 
                      className="transform transition-all duration-500 hover:scale-105 rounded-2xl overflow-hidden"
                    >
                      <OfferCard offer={offer} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div 
                className="text-center py-24 rounded-3xl bg-white/80 backdrop-blur-sm border-2 border-dashed border-slate-300/50 shadow-lg mx-auto max-w-2xl"
              >
                <TagIcon className="mx-auto h-20 w-20 text-slate-300" />
                <h3 className="mt-6 text-xl font-light text-slate-900" style={{ letterSpacing: '0.02em' }}>No offers available yet</h3>
                <p className="mt-3 text-base text-slate-500 font-light">Check back soon for amazing deals!</p>
              </div>
            )}
            
          </div>
        </div>

        {/* Dashboard Showcase Section */}
        <section id="dashboards" className="relative py-32 overflow-hidden" style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 30%, #f1f5f9 60%, #ffffff 100%)'
        }}>
          {/* Organic background blobs */}
          <div className="absolute inset-0 opacity-10">
            <div 
              className="absolute top-1/4 left-1/4 w-[600px] h-[600px] blur-3xl"
              style={{
                clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                transform: 'rotate(45deg)',
                animation: 'float 20s ease-in-out infinite'
              }}
            ></div>
            <div 
              className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] blur-3xl"
              style={{
                clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                transform: 'rotate(-30deg)',
                animation: 'float 18s ease-in-out infinite',
                animationDelay: '2s'
              }}
            ></div>
          </div>

          <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
            {/* Header */}
            <div className="mx-auto max-w-4xl text-center mb-20">
              <h2 className="text-5xl sm:text-6xl md:text-7xl font-light tracking-wide text-slate-900 mb-6" style={{
                letterSpacing: '0.02em'
              }}>
                Powerful Dashboards for Everyone
              </h2>
              <p className="text-xl leading-relaxed text-slate-600 font-light" style={{
                letterSpacing: '0.01em',
                lineHeight: '1.8'
              }}>
                See how Obule works for attendees and organizers
              </p>
            </div>

            {/* Dashboard Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Attendee Dashboard Card */}
              <div className="group relative rounded-3xl bg-white/90 backdrop-blur-md p-8 shadow-xl ring-1 ring-slate-200/50 hover:ring-emerald-200/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl">
                {/* Decorative gradient overlay */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="flex h-14 w-14 items-center justify-center text-white shadow-lg rounded-2xl"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      }}
                    >
                      <UserGroupIcon className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-light text-slate-900 mb-1" style={{ letterSpacing: '0.02em' }}>
                        Attendee Dashboard
                      </h3>
                      <p className="text-sm text-slate-500 font-light">For ticket buyers</p>
                    </div>
                  </div>

                  {/* Dashboard Image */}
                  <div className="relative rounded-2xl overflow-hidden shadow-lg border-4 border-slate-100 group-hover:border-emerald-200 transition-all duration-500">
                    <img 
                      src="/images/dashboards/attendee-dashboard.png" 
                      alt="Attendee Dashboard showing ticket management, order history, and profile settings"
                      className="w-full h-auto object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    {/* Fallback placeholder */}
                    <div className="hidden w-full h-64 bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center flex-col p-8">
                      <UserGroupIcon className="h-16 w-16 text-emerald-300 mb-4" />
                      <p className="text-slate-400 text-sm font-light">Attendee Dashboard Screenshot</p>
                      <p className="text-slate-300 text-xs mt-2">Please upload attendee-dashboard.png</p>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="mt-6 space-y-3">
                    {[
                      'View and manage your tickets',
                      'Track order history',
                      'Manage profile settings',
                      'Browse events and offers'
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-slate-600">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm font-light">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="mt-8">
                    <Link
                      to="/signup"
                      className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium text-white shadow-lg transition-all duration-300 hover:scale-105 active:scale-95"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      }}
                    >
                      <span>Try it now</span>
                      <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* Center Dashboard Card */}
              <div className="group relative rounded-3xl bg-white/90 backdrop-blur-md p-8 shadow-xl ring-1 ring-slate-200/50 hover:ring-blue-200/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl">
                {/* Decorative gradient overlay */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="flex h-14 w-14 items-center justify-center text-white shadow-lg rounded-2xl"
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                      }}
                    >
                      <BuildingOffice2Icon className="h-7 w-7" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-light text-slate-900 mb-1" style={{ letterSpacing: '0.02em' }}>
                        Center Dashboard
                      </h3>
                      <p className="text-sm text-slate-500 font-light">For event organizers</p>
                    </div>
                  </div>

                  {/* Dashboard Image */}
                  <div className="relative rounded-2xl overflow-hidden shadow-lg border-4 border-slate-100 group-hover:border-blue-200 transition-all duration-500">
                    <img 
                      src="/images/dashboards/center-dashboard.png" 
                      alt="Center Dashboard showing event management, analytics, ticket sales, and order tracking"
                      className="w-full h-auto object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    {/* Fallback placeholder */}
                    <div className="hidden w-full h-64 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center flex-col p-8">
                      <ChartBarIcon className="h-16 w-16 text-blue-300 mb-4" />
                      <p className="text-slate-400 text-sm font-light">Center Dashboard Screenshot</p>
                      <p className="text-slate-300 text-xs mt-2">Please upload center-dashboard.png</p>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="mt-6 space-y-3">
                    {[
                      'Manage events and offers',
                      'Track sales and analytics',
                      'View orders and tickets',
                      'Monitor wallet balance'
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-slate-600">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-light">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="mt-8">
                    <Link
                      to="/center/signup"
                      className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium text-white shadow-lg transition-all duration-300 hover:scale-105 active:scale-95"
                      style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                      }}
                    >
                      <span>Get started</span>
                      <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section - Organic */}
        <section className="relative py-32 overflow-hidden" style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #ecfdf5 30%, #d1fae5 60%, #ecfdf5 90%, #ffffff 100%)'
        }}>
          {/* Organic background blobs */}
          <div 
            className="absolute top-0 right-1/4 w-[500px] h-[500px] opacity-10 blur-3xl"
            style={{
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              transform: 'rotate(45deg)',
              animation: 'float 18s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute bottom-0 left-1/4 w-[550px] h-[550px] opacity-10 blur-3xl"
            style={{
              clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              transform: 'rotate(-35deg)',
              animation: 'float 20s ease-in-out infinite',
              animationDelay: '2s'
            }}
          ></div>

          <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
            <div className="mx-auto max-w-4xl text-center mb-20">
              <h2 className="text-5xl sm:text-6xl md:text-7xl font-light tracking-wide text-slate-900 mb-6" style={{
                letterSpacing: '0.02em'
              }}>
                What People Say
              </h2>
              <p className="text-xl text-slate-700 font-light leading-relaxed" style={{
                letterSpacing: '0.01em',
                lineHeight: '1.8'
              }}>
                Real experiences from our community
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                {
                  name: 'Sarah Johnson',
                  role: 'Event Organizer',
                  content: 'Obule has transformed how we manage our events. The platform is intuitive and our attendees love the seamless experience.',
                  rating: 5
                },
                {
                  name: 'Michael Chen',
                  role: 'Music Festival Goer',
                  content: 'I\'ve saved so much money using Obule! The deals are incredible and tickets arrive instantly. Highly recommend!',
                  rating: 5
                },
                {
                  name: 'Emily Rodriguez',
                  role: 'Conference Attendee',
                  content: 'Best ticketing platform I\'ve used. Secure, fast, and the customer support is outstanding. Five stars!',
                  rating: 5
                }
              ].map((testimonial, idx) => (
                <div 
                  key={idx}
                  className="relative p-8 rounded-3xl bg-white/90 backdrop-blur-sm transition-all duration-500 hover:scale-105 border-2 border-emerald-100"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <StarIcon key={i} className="h-5 w-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-base text-slate-700 leading-relaxed font-light mb-6" style={{
                    letterSpacing: '0.01em',
                    lineHeight: '1.7'
                  }}>
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                      }}
                    >
                      <UserGroupIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <div className="text-base font-medium text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-500 font-light">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section - Organic */}
        <section className="relative py-32 overflow-hidden" style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #fef3c7 20%, #fde68a 40%, #fef3c7 60%, #ffffff 100%)'
        }}>
          {/* Organic background blobs */}
          <div 
            className="absolute top-1/2 left-0 w-[600px] h-[600px] opacity-10 blur-3xl -translate-y-1/2"
            style={{
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              transform: 'translate(-25%, -50%) rotate(-20deg)',
              animation: 'float 24s ease-in-out infinite'
            }}
          ></div>
          <div 
            className="absolute top-1/2 right-0 w-[550px] h-[550px] opacity-10 blur-3xl -translate-y-1/2"
            style={{
              clipPath: 'polygon(25% 0%, 75% 0%, 100% 25%, 100% 75%, 75% 100%, 25% 100%, 0% 75%, 0% 25%)',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              transform: 'translate(25%, -50%) rotate(25deg)',
              animation: 'float 22s ease-in-out infinite',
              animationDelay: '3s'
            }}
          ></div>

          <div className="mx-auto max-w-4xl px-6 lg:px-8 relative z-10">
            <div className="mx-auto max-w-3xl text-center mb-20">
              <div 
                className="inline-flex items-center justify-center h-20 w-20 mx-auto mb-6 rounded-3xl text-white shadow-xl"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
                }}
              >
                <QuestionMarkCircleIcon className="h-10 w-10" />
              </div>
              <h2 className="text-5xl sm:text-6xl md:text-7xl font-light tracking-wide text-slate-900 mb-6" style={{
                letterSpacing: '0.02em'
              }}>
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-slate-700 font-light leading-relaxed" style={{
                letterSpacing: '0.01em',
                lineHeight: '1.8'
              }}>
                Everything you need to know about Obule
              </p>
            </div>

            <div className="space-y-6">
              {[
                {
                  question: 'How do I purchase tickets?',
                  answer: 'Simply browse our events, select the tickets you want, and complete the secure checkout process. You\'ll receive your tickets instantly via email.'
                },
                {
                  question: 'Are my payments secure?',
                  answer: 'Absolutely! We use industry-leading encryption and secure payment processing. Your financial information is never stored on our servers.'
                },
                {
                  question: 'Can I get a refund?',
                  answer: 'Yes, we offer flexible refund policies. You can request a refund through your account dashboard, subject to the event organizer\'s refund policy.'
                },
                {
                  question: 'How do I access my tickets?',
                  answer: 'Your tickets are sent to your email immediately after purchase. You can also access them anytime through your Obule account dashboard.'
                },
                {
                  question: 'What payment methods do you accept?',
                  answer: 'We accept all major credit cards, debit cards, and mobile payment options through our secure payment gateway powered by Paystack.'
                },
                {
                  question: 'Can I transfer tickets to someone else?',
                  answer: 'Yes! You can transfer tickets to friends or family directly from your account. Just select the ticket and choose the transfer option.'
                }
              ].map((faq, idx) => (
                <div 
                  key={idx}
                  className="p-8 rounded-2xl bg-white/90 backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] border-2 border-emerald-100"
                >
                  <h3 className="text-xl font-light text-slate-900 mb-4" style={{ letterSpacing: '0.02em' }}>
                    {faq.question}
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed font-light" style={{
                    letterSpacing: '0.01em',
                    lineHeight: '1.7'
                  }}>
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white" aria-labelledby="footer-heading">
        <h2 id="footer-heading" className="sr-only">Footer</h2>
        <div className="mx-auto max-w-7xl px-6 pb-8 pt-20 sm:pt-24 lg:px-8">
          <div className="xl:grid xl:grid-cols-3 xl:gap-12">
            {/* Brand Section */}
            <div className="space-y-6">
              <Link to="/" className="flex items-center gap-1.5 group">
                <img
                  src={Logo}
                  alt="Obule"
                  className="h-10 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
                />
                <span className="text-3xl font-semibold tracking-wide text-white">
                  bule
                </span>
              </Link>
              <p className="text-base leading-relaxed text-slate-400 max-w-sm font-light" style={{
                letterSpacing: '0.01em',
                lineHeight: '1.7'
              }}>
                Event ticketing platform with exclusive discounted offers. Buy tickets at the best prices.
              </p>
              {/* Social Links - Organic Shapes */}
              <div className="flex gap-4">
                {['twitter', 'facebook', 'instagram', 'linkedin'].map((social) => (
                  <a
                    key={social}
                    href={`#${social}`}
                    className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 hover:ring-white/20 transition-all duration-300 hover:scale-110"
                    aria-label={social}
                  >
                    <span className="text-sm font-semibold text-slate-400 hover:text-white transition-colors duration-300 capitalize">
                      {social[0].toUpperCase()}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* Links Grid */}
            <div className="mt-16 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm font-bold leading-6 text-white mb-6">For Attendees</h3>
                  <ul className="space-y-4">
                    <li>
                      <Link to="/events" className="text-sm text-slate-400 hover:text-emerald-400 transition-colors duration-300 flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Browse Events
                      </Link>
                    </li>
                    <li>
                      <Link to="/signup" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Create Account
                      </Link>
                    </li>
                    <li>
                      <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Sign In
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="mt-10 md:mt-0">
                  <h3 className="text-sm font-bold leading-6 text-white mb-6">For Organizers</h3>
                  <ul className="space-y-4">
                    <li>
                      <Link to="/center/signup" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Create Event
                      </Link>
                    </li>
                    <li>
                      <Link to="/center/login" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Organizer Login
                      </Link>
                    </li>
                    <li>
                      <Link to="/pricing" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Pricing
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="md:grid md:grid-cols-2 md:gap-8">
                <div>
                  <h3 className="text-sm font-bold leading-6 text-white mb-6">Support</h3>
                  <ul className="space-y-4">
                    <li>
                      <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Contact Us
                      </Link>
                    </li>
                    <li>
                      <Link to="/help" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Help Center
                      </Link>
                    </li>
                    <li>
                      <Link to="/faq" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        FAQs
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="mt-10 md:mt-0">
                  <h3 className="text-sm font-bold leading-6 text-white mb-6">Legal</h3>
                  <ul className="space-y-4">
                    <li>
                      <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Privacy Policy
                      </Link>
                    </li>
                    <li>
                      <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Terms of Service
                      </Link>
                    </li>
                    <li>
                      <Link to="/refund" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-2 group">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 group-hover:w-3 group-hover:h-3 transition-all duration-300"></span>
                        Refund Policy
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 border-t border-white/10 pt-8 sm:mt-20">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-slate-400">
                &copy; {new Date().getFullYear()} Obule Inc. All rights reserved.
              </p>
              <div className="flex items-center gap-6 text-sm text-slate-400">
                <span className="flex items-center gap-2">
                  <ShieldCheckIcon className="h-4 w-4 text-emerald-400" />
                  Secure Payments
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-400" />
                  Verified Events
                </span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
